import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router'
import { api } from '../api/client'
import type { Sonderfall, SonderfallEbene } from '../api/typen'
import AppRahmen from '../komponenten/AppRahmen'
import { beschreibe, istZugeordnet } from '../komponenten/beschreibung'
import { ebenenLage } from '../komponenten/SonderfallSchritte'

/**
 * Nachtraegliche Einsicht und Pflege eines Sonderfalls: Gesamtfoto und Ebenen in der Reihenfolge
 * ihrer Aufnahme, jeweils mit Foto und Zuordnung. Einzelne Fotos lassen sich neu aufnehmen, der
 * ganze Sonderfall laesst sich loeschen.
 */
export default function SonderfallDetail() {
  const { sonderfallId } = useParams()
  const navigiere = useNavigate()
  const abfragen = useQueryClient()

  const sonderfall = useQuery({
    queryKey: ['sonderfall', sonderfallId],
    queryFn: () => api.get<Sonderfall>(`/api/sonderfaelle/${sonderfallId}`),
  })

  const loeschen = useMutation({
    mutationFn: () => api.loeschen(`/api/sonderfaelle/${sonderfallId}`),
    onSuccess: () => {
      const vpId = sonderfall.data?.versorgungspunktId
      // Verworfener Serverzustand verschwindet auch aus dem Zwischenspeicher.
      abfragen.removeQueries({ queryKey: ['sonderfall', sonderfallId] })
      if (vpId) {
        abfragen.invalidateQueries({ queryKey: ['versorgungspunkt', String(vpId)] })
      }
      abfragen.invalidateQueries({ queryKey: ['erfassung'] })
      navigiere(vpId ? `/versorgungspunkte/${vpId}` : '/', { replace: true })
    },
  })

  const daten = sonderfall.data
  const naechsterSchritt = daten ? offenerSchritt(daten) : null

  return (
    <AppRahmen
      titel={daten?.bezeichnung ?? 'Sonderfall'}
      unter={daten?.sonderfallCode}
      zurueck={daten ? `/versorgungspunkte/${daten.versorgungspunktId}` : '/'}
      pfad={daten?.pfad}
      ebene="sonderfall"
      pfadZusatz={daten?.bezeichnung}
      menue={[
        {
          beschriftung: daten?.gesamtfotoVorhanden ? 'Gesamtfoto ersetzen' : 'Gesamtfoto aufnehmen',
          beiKlick: () => navigiere(`/sonderfaelle/${sonderfallId}/gesamtfoto`),
        },
        {
          beschriftung: 'Sonderfall löschen',
          gefahr: true,
          bestaetigung: {
            frage: 'Gesamtfoto, alle Ebenen und ihre Zuordnungen werden entfernt.',
            bestaetigungstext: 'Endgültig löschen',
          },
          beiKlick: () => loeschen.mutate(),
        },
      ]}
      fuss={
        daten &&
        (naechsterSchritt ? (
          <button className="btn btn-primaer" onClick={() => navigiere(naechsterSchritt.ziel)}>
            {naechsterSchritt.text}
          </button>
        ) : (
          <button
            className="btn btn-primaer"
            onClick={() => navigiere(`/versorgungspunkte/${daten.versorgungspunktId}`)}
          >
            Zurück zum Versorgungspunkt
          </button>
        ))
      }
    >
      {sonderfall.isPending && <div className="spinner" />}

      {daten && (
        <>
          <div className="gruppe">
            <div className="kopfzeile">
              <span className="label">
                {daten.anzahlEbenen} {daten.anzahlEbenen === 1 ? 'Ebene' : 'Ebenen'}
              </span>
              {naechsterSchritt ? (
                <span className="st st-arbeit">unvollständig</span>
              ) : (
                <span className="st st-ok">vollständig</span>
              )}
            </div>
            {daten.beschreibungFreitext && <p className="notiz">{daten.beschreibungFreitext}</p>}
            <span className="sek">
              Erfasst von {daten.erfasstVon} am {new Date(daten.erstelltAm).toLocaleString('de-DE')}
            </span>
          </div>

          {/* Nummeriert, weil P7-02 die Reihenfolge vorgibt: erst das Gesamtfoto, dann die Ebenen. */}
          <section className="gruppe">
            <span className="label">1 · Gesamtfoto</span>
            {daten.gesamtfotoVorhanden ? (
              <img
                src={`/api/sonderfaelle/${daten.id}/gesamtfoto`}
                alt="Gesamtfoto des Sonderfalls"
                style={{ borderRadius: 8, border: '1px solid var(--linie)' }}
              />
            ) : (
              <p className="hinweiszeile">Noch kein Gesamtfoto.</p>
            )}
            <button className="btn-dezent" onClick={() => navigiere(`/sonderfaelle/${daten.id}/gesamtfoto`)}>
              {daten.gesamtfotoVorhanden ? 'Gesamtfoto neu aufnehmen' : 'Gesamtfoto aufnehmen'}
            </button>
          </section>

          <section className="gruppe">
            <span className="label">2 · Ebenen, von oben nach unten</span>
            <span className="sek">Antippen, um ein Foto neu aufzunehmen oder die Zuordnung zu ändern.</span>
            {daten.ebenen.map((ebene) => (
              <button
                key={ebene.ebeneNr}
                className="ebenenzeile"
                onClick={() => navigiere(`/sonderfaelle/${daten.id}/ebenen/${ebene.ebeneNr}`)}
              >
                {ebene.fotoVorhanden ? (
                  <img
                    className="miniatur"
                    src={`/api/sonderfaelle/${daten.id}/ebenen/${ebene.ebeneNr}/foto`}
                    alt={`Ebene ${ebene.ebeneNr}`}
                  />
                ) : (
                  <div className="miniatur leer" aria-hidden="true" />
                )}
                <div className="inh">
                  <b>
                    Ebene {ebene.ebeneNr} · {ebenenLage(ebene.ebeneNr, daten.anzahlEbenen)}
                  </b>
                  <span>{zuordnung(ebene)}</span>
                </div>
                {ebene.fotoVorhanden ? (
                  <span className="st st-ok">Foto</span>
                ) : (
                  <span className="st st-warn">Foto fehlt</span>
                )}
              </button>
            ))}
          </section>

          {loeschen.isError && <p className="hinweiszeile">{(loeschen.error as Error).message}</p>}
        </>
      )}
    </AppRahmen>
  )
}

/**
 * Der naechste offene Schritt in der vorgegebenen Reihenfolge. Fehlt das Gesamtfoto, geht es damit
 * los; sonst mit der ersten Ebene ohne Foto.
 */
function offenerSchritt(sonderfall: Sonderfall): { text: string; ziel: string } | null {
  if (!sonderfall.gesamtfotoVorhanden) {
    return { text: 'Weiter mit dem Gesamtfoto', ziel: `/sonderfaelle/${sonderfall.id}/gesamtfoto?ablauf=1` }
  }
  const offen = sonderfall.ebenen.find((ebene) => !ebene.fotoVorhanden)
  if (offen) {
    return {
      text: `Weiter mit Ebene ${offen.ebeneNr}`,
      ziel: `/sonderfaelle/${sonderfall.id}/ebenen/${offen.ebeneNr}?ablauf=1`,
    }
  }
  return null
}

function zuordnung(ebene: SonderfallEbene): string {
  return istZugeordnet(ebene) ? beschreibe(ebene) : 'noch nicht zugeordnet'
}
